/**
 * Вихід (user-cabinet.md, розд. 10.1; вимога S2 з #336): дія «Вийти» меню користувача.
 *
 * 1. Сервер: `POST /api/v1/auth/sign-out` знищує сесію і cookie (IAM, SEC-025). Мережа чи 5xx —
 *    людина лишається в застосунку з тостом: удати вихід, коли cookie ще дійсна, гірше (спільний ПК).
 *    401 — сесії вже немає, це теж вихід.
 * 2. Клієнт: кеш TanStack Query, поля людини в `cl:prefs` (пояс, стартова…), чернетки (`signOutDrafts`), недавні записи, ознака входу,
 *    `sessionStorage` вкладки, Cache Storage та IndexedDB цього походження (застосунок їх поки не
 *    пише — прибираємо на випадок PWA і сторонніх записів). Налаштування пристрою й вигляд першого кадру (тема, щільність) лишаються.
 * 3. Інші вкладки: подія `sign-out` у `BroadcastChannel` (той самий канал, що в чернеток) — кожна
 *    вкладка прибирає своє і йде на вхід.
 * 4. Повний перехід на вхід (`location.replace`), як на 401: пам'ять сторінки зникає разом із
 *    документом. Сторінку, яку браузер відновив з bfcache кнопкою «Назад» (оболонка — `no-cache`,
 *    тож це можливо), ловить `pageshow` з `persisted`: вміст прихований ще перед переходом, а
 *    відновлена сторінка одразу знову йде на вхід. Її ознака — не лише «входу немає»: після виходу А
 *    і входу Б стара вкладка А бачила б ознаку Б. Тому кожен вихід збільшує лічильник
 *    `SIGN_OUTS_KEY` (пристрій, без даних людини), а вкладка пам'ятає значення на старті.
 * 5. Незбережені зміни без чернетки: діалог «вийти без збереження?» стоїть ДО запиту виходу
 *    (`shell/UserMenu.tsx`, `hasUnsavedChanges`). `leave()` знімає `beforeunload` форм
 *    (`releaseUnloadGuards`), тож після підтвердження браузер своїм діалогом не питає, а людина
 *    не лишається на порожній сторінці без сесії.
 */
import type { QueryClient } from '@tanstack/react-query';
import { resetPersonPrefs } from '../prefs/prefs.ts';
import { SIGN_OUT_CHANNEL, signOutDrafts } from '../ui/save/drafts.ts';
import { releaseUnloadGuards, resetUnloadGuardsForTests } from '../ui/save/unsaved-registry.ts';
import { ApiProblem, api } from './api.ts';
import { clearHandoffs, closeQuickCreate } from './quick-create.ts';
import { forgetRecentOnDevice } from './recent.ts';
import { clearSessionHint, hasSessionHint } from './session.ts';

export const SIGN_OUT_URL = '/api/v1/auth/sign-out';
const LOGIN = '/login';

/**
 * Ключі `localStorage`, які переживають вихід: вигляд і поведінка пристрою, без даних людини й
 * записів (prefs.ts, lite-offer.ts, palette/store.ts, support/browser.ts). Решта ключів застосунку стирається: новий ключ
 * за замовчуванням не переживе виходу. `cl:prefs` — виняток у складі: перед цим `resetPersonPrefs`
 * скидає в ньому поля людини (пояс, стартову, перший день тижня, однолітерні клавіші)
 * і невідомі ключі, лишає `KEPT_ON_SIGN_OUT` (prefs.ts).
 */
export const DEVICE_KEYS: ReadonlySet<string> = new Set([
  'cl:prefs',
  'cl:sign-outs',
  'cl:lite-signals',
  'cl:palette-tip',
  'cl:browser-nudge',
]);

/** Лічильник виходів на цьому пристрої: число, не ПД. Читає й збільшує лише цей файл. */
const SIGN_OUTS_KEY = 'cl:sign-outs';

function readSignOuts(): number {
  try {
    const n = Number(localStorage.getItem(SIGN_OUTS_KEY));
    return Number.isSafeInteger(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Скільки чекати на прибирання Cache Storage / IndexedDB, перш ніж іти на вхід. */
const STORES_TIMEOUT_MS = 1_000;

/** Повний перехід без запису в історії; тести підміняють (jsdom не дає перевизначити `location`). */
let go = (url: string) => location.replace(url);
let leaving = false;
/** Вкладка стартувала з входом: лише тоді чужий вихід і відновлення з bfcache ведуть на вхід. */
let bootSignedIn = false;
/** Лічильник виходів на старті вкладки: інший після повернення з bfcache — був вихід (і вхід). */
let bootSignOuts = 0;

/**
 * Іде «Вийти» (запит, прибирання або перехід): обробники 401 не навігують самі. Інакше перезапит
 * змонтованого екрана після `queryClient.clear()` відправив би на `/login?next=<адреса запису>`
 * і наступна людина отримала б її в `next`.
 */
export function isLeaving(): boolean {
  return leaving;
}

function hideContent(): void {
  // Сторінка, відновлена з bfcache, не покаже даних навіть на мить до переходу.
  document.documentElement.setAttribute('data-signed-out', '');
  const root = document.getElementById('root');
  if (root) root.hidden = true;
}

function clearLocal(): void {
  resetPersonPrefs();
  // Швидке створення: відкритий діалог і передане в повну форму (пам'ять вкладки, DAT-01).
  closeQuickCreate();
  clearHandoffs();
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key !== null && !DEVICE_KEYS.has(key)) localStorage.removeItem(key);
    }
  } catch {
    // Сховище недоступне — у ньому нічого й немає.
  }
  try {
    sessionStorage.clear();
  } catch {
    // нічого
  }
  try {
    localStorage.setItem(SIGN_OUTS_KEY, String(readSignOuts() + 1));
  } catch {
    // Сховища немає — без лічильника лишається ознака входу.
  }
}

async function clearStores(): Promise<void> {
  const tasks: Promise<unknown>[] = [];
  if (typeof caches !== 'undefined') {
    tasks.push(
      caches
        .keys()
        .then((names) => Promise.all(names.map((name) => caches.delete(name))))
        .catch(() => undefined),
    );
  }
  if (typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function') {
    tasks.push(
      indexedDB
        .databases()
        .then((dbs) =>
          Promise.all(
            dbs.map(
              (db) =>
                new Promise<void>((resolve) => {
                  if (!db.name) return resolve();
                  const req = indexedDB.deleteDatabase(db.name);
                  req.onsuccess = req.onerror = req.onblocked = () => resolve();
                }),
            ),
          ),
        )
        .catch(() => undefined),
    );
  }
  await Promise.race([
    Promise.all(tasks),
    new Promise((resolve) => setTimeout(resolve, STORES_TIMEOUT_MS)),
  ]);
}

/**
 * Усе клієнтське, що лишила людина (крім кешу запитів): мітка власника недавніх і їхня черга,
 * ознака входу, поля людини й ключі поза `DEVICE_KEYS`, `sessionStorage`, Cache Storage,
 * IndexedDB. Спільне для «Вийти» і «Вийти» в шарі повторного входу (`relogin.ts`): сесії вже
 * немає, але пристрій лишається наступному.
 */
export async function clearPersonData(): Promise<void> {
  forgetRecentOnDevice();
  clearSessionHint();
  clearLocal();
  await clearStores();
}

/** Прибрати все клієнтське цієї вкладки і повним переходом піти на вхід. */
async function leave(queryClient: QueryClient | null): Promise<void> {
  leaving = true;
  // Сесії вже немає (або вихід підтверджено): браузерне «Залишитися» лишило б порожню сторінку.
  releaseUnloadGuards();
  hideContent();
  try {
    try {
      await queryClient?.cancelQueries();
      queryClient?.clear();
    } finally {
      // Збій кешу запитів не пропускає прибирання сховищ (#408): інакше вкладка пішла б на вхід,
      // лишивши наступній людині поля, недавні й `sessionStorage` попередньої.
      await clearPersonData();
    }
  } finally {
    // Збій прибирання не лишає вкладку на прихованій сторінці: 401-обробник під час виходу мовчить
    // (`isLeaving`), тож перехід на вхід — лише звідси.
    go(LOGIN);
  }
}

/**
 * «Вийти». `false` — сервер не підтвердив вихід (мережа, 5xx): людина лишається в застосунку,
 * показ помилки — на викликачі. Успіх не повертається: документ іде на вхід.
 */
export async function signOut(queryClient: QueryClient): Promise<boolean> {
  if (leaving) return true;
  leaving = true;
  try {
    await api('POST', SIGN_OUT_URL);
  } catch (error) {
    if (!(error instanceof ApiProblem && error.status === 401)) {
      leaving = false;
      return false;
    }
  }
  // Спершу інші вкладки (подія йде через сам `signOutDrafts`), далі — своя.
  signOutDrafts();
  await leave(queryClient);
  return true;
}

/**
 * Старт застосунку: вихід в іншій вкладці і повернення з bfcache після виходу ведуть на вхід.
 * Вкладка без входу не реагує.
 */
export function watchSignOut(queryClient: QueryClient): () => void {
  bootSignedIn = hasSessionHint();
  bootSignOuts = readSignOuts();
  const channel =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SIGN_OUT_CHANNEL);
  if (channel) {
    channel.addEventListener('message', (event: MessageEvent<{ type?: string }>) => {
      if (event.data?.type !== 'sign-out' || leaving || !bootSignedIn) return;
      void leave(queryClient);
    });
  }
  const onPageShow = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    if (leaving || (bootSignedIn && (!hasSessionHint() || readSignOuts() !== bootSignOuts))) {
      // Поля попередньої людини не переживуть повернення: вихід міг статися в іншій вкладці.
      resetPersonPrefs();
      hideContent();
      go(LOGIN);
    }
  };
  window.addEventListener('pageshow', onPageShow);
  return () => {
    channel?.close();
    window.removeEventListener('pageshow', onPageShow);
  };
}

/** Лише для тестів. */
export function resetSignOutForTests(navigate: (url: string) => void): void {
  leaving = false;
  bootSignedIn = false;
  bootSignOuts = 0;
  resetUnloadGuardsForTests();
  go = navigate;
}
